import * as React from "react";
import * as ReactDOM from "react-dom";
import { EventManager } from "./globals";
import { PopupTrigger } from "./";

interface IProperties
{
    /**
     * Inline-Style.
     */
    style?: React.CSSProperties,

    /**
     * Enthält alle Child-Elemente.
     */
    children: JSX.Element | JSX.Element[],

    /**
     * Das eindeutige Kennzeichen eines Popups.
     */
    id: string,

    /**
     * Wenn angegeben, rendert der PopupProvider mit diesem Tag.
     */
    renderTag?: string,

    /**
     * CSS-Klasse.
     */
    className?: string,

    /**
     * Gibt an, wann der PopupProvider ausgelöst werden soll.
     */
    trigger?: PopupTrigger,

    /**
     * Gibt die Dauer bis zum öffnen des Popups an.
     */
    triggerDelay?: number,

    /**
     * Angabe, ob die Standard Funktion des Browser nicht ausgeführt werden soll.
     */
    triggerPreventDefault?: boolean,

    /**
     * Angabe, ob das Ereignis nicht zum Parent Element durchgereicht werden soll.
     */
    triggerStopPropagation?: boolean,

    /**
     * Gibt an welcher Text im Attribut "data-popup-provider".
     */
    attributeName?: string,

    /**
     * Hiermit kann eine benutzerdefinierte "ref" Eigenschaft angegeben werden.
     */
    refPropName?: string

    /**
     * Hiermit kann ein Datensatz angehängt werden. Dieser wird dann beim Ereignis "onOpening" mit übergeben.
     */
    dataSource?: any,

    /**
     * Angabe, ob die trigger abgeschaltet sind.
     */
    isDisabled?: boolean,

    /**
     * Wird ausgeführt bevor sich das Popup öffnet.
     */
    onOpening?: (e: MouseEvent, dataSource?: any) => boolean,

    /**
     * Wird ausgeführt bevor sich das Popup schließt.
     */
    onClosing?: () => boolean
}

export class PopupProvider extends React.Component<IProperties>
{
    /**
     * Enthält die ID des Timeouts wenn ein Popup via MouseEnter geöffnet werden soll
     * und ein TriggerDelay angegeben wurde.
     */
    private _hoverTimeoutId: number;

    /**
     * Standardwerte für die Eigenschaften setzen.
     */
    public static defaultProps: Partial<IProperties> = {
        renderTag: "div",
        trigger: PopupTrigger.RightClick,
        triggerDelay: 1000,
        triggerPreventDefault: false,
        triggerStopPropagation: false,
        refPropName: "ref"
    };

    /**
     * Erstellt eine neue Instanz der Komponente @see PopupProvider.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        EventManager.on(`hide:${this.props.id}`, props.onClosing);
        EventManager.on("abort", this.abort);
    }

    /**
     * Wird ausgelöst sobald der angegebene "trigger" zutrifft.
     * @param e Das Ereignis welches diese Funktion ausgelöst hat.
     */
    private handleEvent = async (e: React.MouseEvent<HTMLElement>) =>
    {
        const { trigger, triggerDelay, triggerPreventDefault, triggerStopPropagation, dataSource, isDisabled, onOpening } = this.props;
        const event = { ...e };

        // Wenn das Ereignis nicht zu den parent Elements durchgereicht werden soll.
        if (triggerPreventDefault || trigger === PopupTrigger.RightClick)
            e.preventDefault();

        if (triggerStopPropagation)
            e.stopPropagation();

        if (isDisabled)
            return;

        // Die "onOpening" Eigenschaft bevor das Popup öffnet, auslösen.
        var show: boolean = onOpening && onOpening(event.nativeEvent, dataSource);
        if (show != null && !show)
            return;

        // Wenn der "trigger" auf "Hover" steht und der "triggerDelay" > 0 ist, dann
        // soll ein Timeout gesetzt werden und dann erst das Popup geöffnet werden .
        if (trigger === PopupTrigger.Hover && triggerDelay > 0)
        {
            this._hoverTimeoutId = setTimeout(() =>
            {
                this.emitShow(event);

            }, triggerDelay);
        }
        else
        {
            this.emitShow(event);
        }
    }

    /**
     * Führt das "show" Ereignis aus. Das Popup hört auf dieses um zu wissen wann es geöffnet werden soll.
     * @param e Das Ereignis welches diese Funktion ausgelöst hat.
     */
    private emitShow(e: React.MouseEvent<HTMLElement>)
    {
        const { id, trigger, triggerDelay, dataSource } = this.props;

        if (trigger === PopupTrigger.Click || trigger === PopupTrigger.DoubleClick || trigger === PopupTrigger.Hover)
        {
            EventManager.emit(`show:${id}`, e, trigger, triggerDelay, dataSource);
        }
        else
        {
            EventManager.emit("abort", e);
            EventManager.emit(`show:${id}`, e, trigger, triggerDelay, dataSource);
        }
    }

    /**
     * Wird ausgeführt sobald der Mauszeiger das umschlossene Element verlässt.
     * @param e Das Ereignis welches diese Funktion ausgelöst hat.
     */
    private handleMouseLeave = (e: React.MouseEvent<HTMLElement>) =>
    {
        if (this._hoverTimeoutId > 0)
        {
            clearTimeout(this._hoverTimeoutId);
            EventManager.emit(`hideOnMouseLeave:${this.props.id}`);
        }
    }

    /**
     * Wird ausgeführt, sobald mit der rechten Maustaste auf ein PopupProvider geklickt wird.
     * @param e Das Ereignis welches diese Funktion ausgelöst hat.
     */
    private abort = (e: React.MouseEvent<HTMLElement>) =>
    {
        if (this.props.trigger === PopupTrigger.Hover)
        {
            clearTimeout(this._hoverTimeoutId);
        }
    }

    ///**
    // * Ermittelt die Child-Elemente des PopupProviders.
    // */
    //private getChildren(children)
    //{
    //    //this._refs = {};
    //    return React.Children.map(children, (child) =>
    //    {
    //        var attr: any = {
    //            [this.props.refPropName]: (ref) =>
    //            {
    //                //let index = Object.keys(this._refs).length;
    //                if (ref != null)
    //                {
    //                    this._refs[this.props.childName] = ref;
    //                }
    //            }
    //        };

    //        if (React.isValidElement(child))
    //        {
    //            return React.cloneElement(child, attr);
    //        }
    //        else
    //        {
    //            return child;
    //        }
    //    });
    //}

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const {
            style,
            className,
            renderTag,
            trigger,
            children,
            attributeName
        } = this.props;

        const props = {
            style: style,
            ["data-popup-provider"]: (attributeName == null ? "" : attributeName),
            className: className ? className : null,
            [trigger]: this.handleEvent,
            onMouseLeave: trigger === PopupTrigger.Hover ? this.handleMouseLeave : null,
        };

        //if (trigger === PopupTrigger.Hover)
        //{
        //    props.onMouseLeave = this.handleMouseLeave;
        //}

        return React.createElement(renderTag, props, children);
    }
}